import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CarCard from "@/components/CarCard";
import { Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Fire-and-forget analytics beacon. Never blocks the caller and swallows
 * errors — the homepage should never break because a stats POST failed.
 */
function trackFeaturedEvent(kind, carId, variant) {
  try {
    api.post("/featured/event", { kind, car_id: carId, variant }).catch(() => {});
  } catch (_e) {
    /* silently ignored */
  }
}

/**
 * FeaturedListings
 * ----------------
 * Homepage section that surfaces admin-picked (or paid) featured cars.
 *
 * Two visual variants, driven by `variant`:
 *   • "teaser"  — compact 4-up strip that lives near the top of the homepage
 *                 (below TopDestinationsChipRow). Self-hides when the API
 *                 returns nothing, so we never render an empty section.
 *   • "grid"    — full "showcase" grid (default 8 cars) placed mid-page.
 *
 * Uses the existing `CarCard` so the visual language matches Browse and the
 * "Popular near you" trending section directly below it — the section header
 * and the amber Featured badge are what set it apart.
 */
export default function FeaturedListings({
  variant = "grid",
  limit,
  headline,
  kicker = "Featured listings",
  showSeeAll = true,
  className = "",
  autoPlay = true,             // Iter 70h — teaser carousel autoplay
  autoPlayIntervalMs = 5000,
}) {
  const [cars, setCars] = useState(null); // null while loading
  // Iter 70b — impression telemetry. Fire once per car per section render
  // using an IntersectionObserver so we only count in-viewport views.
  const observedRef = useRef(new Set());

  const effectiveLimit = limit ?? (variant === "teaser" ? 8 : 8);
  const effectiveHeadline =
    headline ?? (variant === "teaser" ? "Handpicked for you" : "Top featured cars");

  // Iter 70f — teaser variant is a horizontal carousel; the grid variant
  // stays as-is because a mid-page showcase reads better in one clean
  // 4-column block.
  const railRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = () => {
    const el = railRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollByStep = (dir) => {
    const el = railRef.current;
    if (!el) return;
    // Advance by roughly one card-width so users see a satisfying "next slide"
    // rather than a jarring full-page jump.
    const step = Math.max(280, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  useEffect(() => {
    if (variant !== "teaser") return;
    const el = railRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
    };
  }, [variant, cars]);

  // Iter 70h — autoplay. Advances one step every `autoPlayIntervalMs`; pauses
  // on hover/focus and while the tab is hidden. Wraps back to the start when
  // the rail hits its right edge so every featured car gets equal airtime.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (variant !== "teaser" || !autoPlay) return;
    if (!cars || cars.length < 2) return;      // no point auto-advancing a single card
    if (paused) return;
    if (typeof document !== "undefined" && document.hidden) return;

    const el = railRef.current;
    if (!el) return;

    const tick = () => {
      if (!el) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        scrollByStep(1);
      }
    };
    const id = setInterval(tick, autoPlayIntervalMs);

    const onVisibility = () => {
      // Force re-run of the effect when tab visibility flips so the interval
      // re-arms in the foreground.
      setPaused((p) => (document.hidden ? true : p && false));
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [variant, autoPlay, autoPlayIntervalMs, cars, paused]);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/featured-cars?limit=${effectiveLimit}`)
      .then((r) => {
        if (!cancelled) setCars(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setCars([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveLimit]);

  // Iter 70b — set up IntersectionObserver once cars are known.
  useEffect(() => {
    if (!cars || cars.length === 0 || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const carId = entry.target.getAttribute("data-featured-car-id");
          if (!carId || observedRef.current.has(carId)) return;
          observedRef.current.add(carId);
          trackFeaturedEvent("impression", carId, variant);
        });
      },
      { threshold: 0.5 },
    );
    // Observe every rendered card wrapper.
    document
      .querySelectorAll(`[data-featured-section='${variant}'] [data-featured-car-id]`)
      .forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [cars, variant]);

  // Self-hide when nothing is featured — no visual clutter on fresh installs.
  if (!cars || cars.length === 0) return null;

  const padding =
    variant === "teaser" ? "pt-14 pb-6" : "pt-20 pb-12";

  return (
    <section
      className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${padding} ${className}`}
      data-testid={`featured-listings-${variant}`}
    >
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold mb-3">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {kicker}
          </div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">
            {effectiveHeadline}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {variant === "teaser" && (
            <div className="hidden sm:flex items-center gap-1.5" data-testid="featured-listings-carousel-controls">
              <button
                type="button"
                onClick={() => scrollByStep(-1)}
                disabled={!canPrev}
                aria-label="Previous featured cars"
                data-testid="featured-listings-prev"
                className="h-10 w-10 rounded-full border border-neutral-200 bg-white text-neutral-700 hover:border-amber-400 hover:text-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center shadow-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollByStep(1)}
                disabled={!canNext}
                aria-label="Next featured cars"
                data-testid="featured-listings-next"
                className="h-10 w-10 rounded-full border border-neutral-200 bg-white text-neutral-700 hover:border-amber-400 hover:text-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center shadow-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          {showSeeAll && variant === "grid" && (
            <Link
              to="/cars?featured=1"
              className="text-sm font-medium text-blue-600 hover:underline"
              data-testid="featured-listings-see-all"
            >
              See all →
            </Link>
          )}
        </div>
      </div>

      {variant === "teaser" ? (
        <div
          className="relative"
          data-featured-section={variant}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
          data-autoplay-paused={paused ? "true" : "false"}
        >
          <div
            ref={railRef}
            className="flex flex-col sm:flex-row gap-6 sm:overflow-x-auto sm:snap-x sm:snap-mandatory sm:scroll-smooth pb-2 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="featured-listings-rail"
          >
            {cars.map((c, i) => (
              <div
                key={c.id}
                data-featured-car-id={c.id}
                onClickCapture={() => trackFeaturedEvent("click", c.id, variant)}
                className="sm:snap-start sm:flex-none w-full sm:w-[300px] lg:w-[320px]"
              >
                <CarCard car={c} index={i} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10"
          data-featured-section={variant}
        >
          {cars.map((c, i) => (
            <div
              key={c.id}
              data-featured-car-id={c.id}
              onClickCapture={() => trackFeaturedEvent("click", c.id, variant)}
            >
              <CarCard car={c} index={i} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
